import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import inventoryService, {
  InventoryCategory,
  InventoryCategoryFilters,
  InventoryHistoryEntry,
  InventoryItem,
  InventoryHistoryFilters,
  InventoryItemFilters,
} from "../../services/inventoryService";

interface PaginationState {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface InventoryState {
  categories: InventoryCategory[];
  items: InventoryItem[];
  history: InventoryHistoryEntry[];
  selectedCompanyId: string | null;
  categoriesPagination: PaginationState;
  itemsPagination: PaginationState;
  historyPagination: PaginationState;
  isLoadingCategories: boolean;
  isLoadingItems: boolean;
  isLoadingHistory: boolean;
  isMutating: boolean;
  error: string | null;
}

const defaultPaginationState = (): PaginationState => ({
  page: 1,
  limit: 20,
  totalCount: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false,
});

const initialState: InventoryState = {
  categories: [],
  items: [],
  history: [],
  selectedCompanyId: null,
  categoriesPagination: defaultPaginationState(),
  itemsPagination: defaultPaginationState(),
  historyPagination: defaultPaginationState(),
  isLoadingCategories: false,
  isLoadingItems: false,
  isLoadingHistory: false,
  isMutating: false,
  error: null,
};

export const fetchInventoryCategories = createAsyncThunk(
  "inventory/fetchCategories",
  async (params: InventoryCategoryFilters) => {
    const response = await inventoryService.getCategories(params);
    return { ...response, companyId: params.companyId };
  }
);

export const fetchInventoryItems = createAsyncThunk(
  "inventory/fetchItems",
  async (filters: InventoryItemFilters) => {
    const response = await inventoryService.getItems(filters);
    return { ...response, companyId: filters.companyId };
  }
);

export const fetchInventoryHistory = createAsyncThunk(
  "inventory/fetchHistory",
  async (filters: InventoryHistoryFilters) => {
    const response = await inventoryService.getHistory(filters);
    return { ...response, companyId: filters.companyId };
  }
);

export const createInventoryCategory = createAsyncThunk(
  "inventory/createCategory",
  async (data: { companyId: string; title: string; description?: string; isActive?: boolean }) => {
    const response = await inventoryService.createCategory(data);
    return response.data;
  }
);

export const updateInventoryCategory = createAsyncThunk(
  "inventory/updateCategory",
  async ({
    id,
    data,
  }: {
    id: string;
    data: { companyId: string; title?: string; description?: string; isActive?: boolean };
  }) => {
    const response = await inventoryService.updateCategory(id, data);
    return response.data;
  }
);

export const deleteInventoryCategory = createAsyncThunk(
  "inventory/deleteCategory",
  async ({ id, companyId }: { id: string; companyId: string }) => {
    await inventoryService.deleteCategory(id, companyId);
    return id;
  }
);

export const createInventoryItem = createAsyncThunk(
  "inventory/createItem",
  async (data: {
    companyId: string;
    categoryId: string;
    title: string;
    description?: string;
    price?: number;
    quantity?: number;
    initialCost?: number;
    isActive?: boolean;
  }) => {
    const response = await inventoryService.createItem(data);
    return response.data;
  }
);

export const updateInventoryItem = createAsyncThunk(
  "inventory/updateItem",
  async ({
    id,
    data,
  }: {
    id: string;
    data: {
      companyId: string;
      categoryId?: string;
      title?: string;
      description?: string;
      price?: number;
      quantity?: number;
      isActive?: boolean;
    };
  }) => {
    const response = await inventoryService.updateItem(id, data);
    return response.data;
  }
);

export const deleteInventoryItem = createAsyncThunk(
  "inventory/deleteItem",
  async ({ id, companyId }: { id: string; companyId: string }) => {
    await inventoryService.deleteItem(id, companyId);
    return id;
  }
);

export const addInventoryStock = createAsyncThunk(
  "inventory/addStock",
  async ({
    id,
    data,
  }: {
    id: string;
    data: { companyId: string; quantity: number; cost?: number; note?: string };
  }) => {
    const response = await inventoryService.addStock(id, data);
    return response.data;
  }
);

export const useInventoryItem = createAsyncThunk(
  "inventory/useItem",
  async ({
    id,
    data,
  }: {
    id: string;
    data: {
      companyId: string;
      quantity: number;
      maintenanceId?: string;
      note?: string;
      priceOverride?: number;
    };
  }) => {
    const response = await inventoryService.useItem(id, data);
    return response.data;
  }
);

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    clearInventoryError: (state) => {
      state.error = null;
    },
    resetInventoryState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInventoryCategories.pending, (state) => {
        state.isLoadingCategories = true;
        state.error = null;
      })
      .addCase(fetchInventoryCategories.fulfilled, (state, action) => {
        state.isLoadingCategories = false;
        state.categories = action.payload.data;
        state.selectedCompanyId = action.payload.companyId;
        state.categoriesPagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.pagination.totalPages,
          hasNext: action.payload.pagination.hasNext,
          hasPrev: action.payload.pagination.hasPrev,
        };
      })
      .addCase(fetchInventoryCategories.rejected, (state, action) => {
        state.isLoadingCategories = false;
        state.error = action.error.message || "Failed to fetch inventory categories";
      })
      .addCase(fetchInventoryItems.pending, (state) => {
        state.isLoadingItems = true;
        state.error = null;
      })
      .addCase(fetchInventoryItems.fulfilled, (state, action) => {
        state.isLoadingItems = false;
        state.items = action.payload.data;
        state.selectedCompanyId = action.payload.companyId;
        state.itemsPagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.pagination.totalPages,
          hasNext: action.payload.pagination.hasNext,
          hasPrev: action.payload.pagination.hasPrev,
        };
      })
      .addCase(fetchInventoryItems.rejected, (state, action) => {
        state.isLoadingItems = false;
        state.error = action.error.message || "Failed to fetch inventory items";
      })
      .addCase(fetchInventoryHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchInventoryHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        state.history = action.payload.data;
        state.selectedCompanyId = action.payload.companyId;
        state.historyPagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.pagination.totalPages,
          hasNext: action.payload.pagination.hasNext,
          hasPrev: action.payload.pagination.hasPrev,
        };
      })
      .addCase(fetchInventoryHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.error.message || "Failed to fetch inventory history";
      });

    builder
      .addMatcher(
        (action) =>
          action.type.startsWith("inventory/") &&
          action.type.endsWith("/pending") &&
          !action.type.includes("fetch"),
        (state) => {
          state.isMutating = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("inventory/") &&
          action.type.endsWith("/fulfilled") &&
          !action.type.includes("fetch"),
        (state) => {
          state.isMutating = false;
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("inventory/") &&
          action.type.endsWith("/rejected") &&
          !action.type.includes("fetch"),
        (state, action: any) => {
          state.isMutating = false;
          state.error =
            action.payload || action.error?.message || "Inventory operation failed";
        }
      );
  },
});

export const { clearInventoryError, resetInventoryState } = inventorySlice.actions;

export default inventorySlice.reducer;
